import { RootState } from 'store';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';

import { CurtainHeader } from 'curtain/components/CurtainHeader';
import { ReactComponent as ArrowUpIcon } from 'assets/icons/arrow-up.svg';

import { clearCurtain } from 'curtain/store/curtainActions';

import { lynch, mineShaft, nevada } from 'shared/config/Colors';

import SVG from 'shared/components/SVG';
import ScrollbarsCustom from 'shared/components/ScrollbarsCustom';
import { ButtonIcon } from 'shared/components/Button';
import { H3, PSmall } from 'shared/components/Typography';
import { Spacer } from 'shared/components/Layout';
import { Wrapper } from 'shared/components/Wrapper';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import styled from 'styled-components';
import { getWarnings, setWarnings } from 'order/warnings/store/warningsActions';
import { useParams } from 'react-router';
import { OrderPageParams } from 'order/interfaces/OrderPageParams';
import { StyleWarning } from 'order/warnings/interface/Wranings';
import { ItemRow } from 'order/components/OrderLogs/components/ItemRow';
import StyleWarnings from 'order/components/OrderLogs/components/Warnings/StyleWarnings';
import Loader from 'shared/components/Loader';
import { StyleHeader } from 'shared/components/StyleHeader';
import {
  getWritingHints,
  setWritingHints,
} from 'order/writingHints/store/writingHintsActions';
import { StyleNotesContainer } from 'order/components/OrderLogs/components/WritingHints/components/StyleNotesContainer';
import { useHasPermissions } from 'shared/hooks/useHasPermissions';
import { userPermissionsValues } from 'shared/enum/userPermissionsEnum';

interface Sections {
  warnings: boolean;
  writingHints: boolean;
}

const TabContainer = styled(Wrapper)`
  // Total page height minus header and footer heights plus bottom-border height
  height: 100%;
  overflow: auto;
`;

const Header = styled(PSmall)`
  padding: 16px 32px;
  border-bottom: 1px solid ${({ theme }) => theme.athensGray};
`;

export const HintsTab = () => {
  const dispatch = useAppDispatch();

  const [toggleSections, setToggleSections] = useState<Sections>({
    warnings: true,
    writingHints: true,
  });

  const onToggleSectionsHandler = (section: keyof Sections) => {
    setToggleSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  const { orderId } = useParams<OrderPageParams>();
  const isUserCSR = useHasPermissions(
    userPermissionsValues.CSR_VIEW_ORDERBOARD
  );

  const [warningsLoading, setWarningsLoading] = useState(true);
  const [writingHintsLoading, setWritingHintsLoading] = useState(true);

  const warnings = useSelector(
    (state: RootState) => state.warningsReducer.warnings
  );

  const writingHints = useSelector(
    (state: RootState) => state.writingHintsReducer.writingHints
  );

  const filterEmptyStyles = (styleWarning: StyleWarning) => {
    const hasStyleWarnings = styleWarning.warnings.length > 0;

    const hasLineItemWarnings = styleWarning.lineItemWarnings.some(
      (lineItemWarning) =>
        lineItemWarning.reason.length > 0 ||
        lineItemWarning.warnings.length > 0 ||
        lineItemWarning.modificationWarnings.some(
          (modWarning) =>
            modWarning.reason.length > 0 || modWarning.warnings.length > 0
        )
    );

    return hasStyleWarnings || hasLineItemWarnings;
  };

  useEffect(() => {
    dispatch(getWarnings(orderId, setWarningsLoading));
    dispatch(getWritingHints(orderId, setWritingHintsLoading));

    return () => {
      dispatch(setWarnings(null));
      dispatch(setWritingHints(null));
    };
  }, []);

  return (
    <Wrapper style={{ height: '100%' }}>
      <TabContainer>
        <ScrollbarsCustom
          autoHide={false}
          autoHideTimeout={500}
          autoHideDuration={300}
          paddingRight={0}
        >
          {/* Tab Heading */}
          <CurtainHeader flex middle between>
            <Wrapper flex gap={12}>
              <H3>Hints</H3>
            </Wrapper>

            <Wrapper flex middle>
              <ButtonIcon onClick={() => dispatch(clearCurtain())}>
                <SVG icon={<CloseIcon />} hoverColor={lynch} />
              </ButtonIcon>
            </Wrapper>
          </CurtainHeader>

          {/* Warnings Section */}
          <CurtainHeader
            flex
            middle
            between
            onClick={() => onToggleSectionsHandler('warnings')}
            className={toggleSections.warnings ? 'expanded' : ''}
          >
            <Wrapper flex width={40}>
              <H3>Warnings</H3>
            </Wrapper>
            <Wrapper flex middle>
              <SVG
                icon={<ArrowUpIcon className="arrow-up" />}
                color={mineShaft}
              />
            </Wrapper>
          </CurtainHeader>

          {warningsLoading && toggleSections.warnings && <Loader />}

          {!warningsLoading && toggleSections.warnings && (
            <Wrapper>
              {warnings && warnings?.warnings?.length > 0 && (
                <div>
                  <StyleHeader fontWeight={600} fontSize={16}>
                    Order Warnings
                  </StyleHeader>
                </div>
              )}

              {warnings?.warnings.map((orderWarning) => (
                <ItemRow key={`${orderWarning.entityId}`}>
                  <PSmall fontWeight={600}>{orderWarning.message}</PSmall>
                </ItemRow>
              ))}

              {warnings?.styleWarnings
                .filter(filterEmptyStyles)
                .map((styleWarning) => (
                  <StyleWarnings
                    key={styleWarning.id}
                    styleWarning={styleWarning}
                  />
                ))}
            </Wrapper>
          )}

          {/* Writing Hints Section */}
          {isUserCSR && (
            <Wrapper>
              <CurtainHeader
                flex
                middle
                between
                onClick={() => onToggleSectionsHandler('writingHints')}
                className={toggleSections.writingHints ? 'expanded' : ''}
              >
                <Wrapper flex width={40}>
                  <H3>Writing Hints</H3>
                </Wrapper>
                <Wrapper flex middle>
                  <SVG
                    icon={<ArrowUpIcon className="arrow-up" />}
                    color={mineShaft}
                  />
                </Wrapper>
              </CurtainHeader>

              {writingHintsLoading && toggleSections.writingHints && <Loader />}

              {!writingHintsLoading && toggleSections.writingHints && (
                <Wrapper>
                  {(writingHints?.dealershipPreferencesResponse.length ?? 0) >
                  0 ? (
                    <>
                      <Header bold>Dealer Notices for this Order</Header>

                      {writingHints?.dealershipPreferencesResponse.map(
                        (preference, index) => (
                          <ItemRow>
                            <PSmall color={nevada}>{index + 1}</PSmall>
                            <PSmall color={nevada}>{preference}</PSmall>
                          </ItemRow>
                        )
                      )}
                    </>
                  ) : null}

                  {writingHints?.orderNotesResponse.styleNoteResponses.map(
                    (styleNotes) => {
                      return (
                        <StyleNotesContainer
                          styleNotes={styleNotes}
                          writingHints={writingHints}
                        />
                      );
                    }
                  )}
                </Wrapper>
              )}
            </Wrapper>
          )}

          {/* Footer Spacing */}
          <Spacer h="20px" />
        </ScrollbarsCustom>
      </TabContainer>
    </Wrapper>
  );
};
